import { Accordion } from "@primitives/accordion";
import { ScrollArea } from "@primitives/scroll-area";
import { useTranslation } from "react-i18next";

import AppearanceTab from "./appearance/AppearanceTab";
import LanguageTab from "./language/LanguageTab";
import PreferencesTab from "./preferences/PreferencesTab";
import { useMemo } from "react";
import { useAvatarUploader } from "@/middleware/hooks/useAvatarUploader";
import AvatarUploader from "@/components/shared/AvatarUploader";
import { Avatar, AvatarFallback, AvatarImage } from "@primitives/avatar";
import { useAuthState } from "@/zustand/AuthState";
import AccountTab from "./account/AccountTab";
import DeleteAccount from "./DeleteAccount";
import useUserAvatar from "@/middleware/hooks/useUserAvatar";
import { processAvatarBucketUrl, stringToRgbColor } from "@/lib/utils";
import { Badge } from "@primitives/badge";
import { useSubscriptionState } from "@/zustand/SubscriptionState";
import PaymentsTab from "./payments/PaymentsTab";
import NavRow from "./NavRow";
import { CATEGORY_LABEL_KEY } from "./categoryLabels";
import {
  CircleHelp,
  Coins,
  CreditCard,
  Languages,
  Palette,
  Receipt,
  ShieldCheck,
  SlidersHorizontal,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import { featureFlags } from "@/config/featureFlags";
import {
  ChatUserAction,
  IMPLEMENTED_SETTINGS_CATEGORIES,
  SettingsCategory,
  useNavigationState,
} from "@/zustand/NavigationState";
import { useStartSettingsCategoryView } from "@/hooks/useStartSettingsCategoryView";
import { useStartSubscriptionView } from "@/hooks/useStartSubscriptionView";
import { useStartAddonCreditsView } from "@/hooks/useStartAddonCreditsView";
import { useStartInvoicesView } from "@/hooks/useStartInvoicesView";
import { useStartPaymentsFaqView } from "@/hooks/useStartPaymentsFaqView";

// Leading row icons (decorative; labels carry the meaning) — the settings-list
// convention of the leading mobile messengers.
const CATEGORY_ICON: Record<SettingsCategory, LucideIcon> = {
  profile: UserRound,
  security: ShieldCheck,
  appearance: Palette,
  language: Languages,
  preferences: SlidersHorizontal,
};

/**
 * Flat settings menu (flag `settingsMiddleViews`): every destination is a
 * standalone row opening a middle-column view. The Payments group is dissolved
 * into its four rows; those keep their existing actions/hooks unchanged
 * (docs/settings-middle-views/design.md).
 */
function FlatSettingsList() {
  const { t } = useTranslation();
  const activeChatUserAction = useNavigationState(
    (s) => s.activeChatUserAction,
  );
  const activeSettingsCategory = useNavigationState(
    (s) => s.activeSettingsCategory,
  );

  const startSettingsCategoryView = useStartSettingsCategoryView();
  const startSubscriptionView = useStartSubscriptionView();
  const startAddonCreditsView = useStartAddonCreditsView();
  const startInvoicesView = useStartInvoicesView();
  const startPaymentsFaqView = useStartPaymentsFaqView();

  // Read-side single source of truth for the highlight: a settings row is
  // active only when BOTH the action and its category match — payments
  // actions never touch activeSettingsCategory, so a stale category alone
  // must never light a row.
  const isCategoryActive = (category: SettingsCategory) =>
    activeChatUserAction === ChatUserAction.VIEW_SETTINGS_CATEGORY &&
    activeSettingsCategory === category;

  return (
    <div className="flex w-full flex-col gap-1">
      {IMPLEMENTED_SETTINGS_CATEGORIES.map((category) => (
        <NavRow
          key={category}
          icon={CATEGORY_ICON[category]}
          label={t(CATEGORY_LABEL_KEY[category])}
          isActive={isCategoryActive(category)}
          onClick={() => startSettingsCategoryView(category)}
        />
      ))}
      <NavRow
        icon={CreditCard}
        label={t("subscription.pageTitle")}
        isActive={activeChatUserAction === ChatUserAction.VIEW_SUBSCRIPTION}
        onClick={startSubscriptionView}
      />
      <NavRow
        icon={Coins}
        label={t("payments.addonCredits")}
        isActive={activeChatUserAction === ChatUserAction.VIEW_ADDON_CREDITS}
        onClick={startAddonCreditsView}
      />
      <NavRow
        icon={Receipt}
        label={t("subscription.billingHistory")}
        isActive={activeChatUserAction === ChatUserAction.VIEW_INVOICES}
        onClick={startInvoicesView}
      />
      <NavRow
        icon={CircleHelp}
        label={t("payments.faq")}
        isActive={activeChatUserAction === ChatUserAction.VIEW_PAYMENTS_FAQ}
        onClick={startPaymentsFaqView}
      />
    </div>
  );
}

export default function SettingsView() {
  const { t } = useTranslation();
  const username = useAuthState((state) => state.username);
  const avatarAttachmentId = useUserAvatar(username);
  const avatarUrl = avatarAttachmentId
    ? processAvatarBucketUrl(avatarAttachmentId)
    : "";
  const avatarColor = useMemo(() => stringToRgbColor(username), [username]);
  const { isUploading, blobUrl, handleFileChange, deleteAvatar } =
    useAvatarUploader({
      updateUserAvatarCallback: true,
    });
  const plan = useSubscriptionState((state) => state.subscription.plan);

  const PLAN_LABELS: Record<string, string> = {
    free: "Free",
    standard: "Standard",
    pro: "Pro",
    proplus: "Pro+",
    premium: "Premium",
  };

  return (
    <div className="relative flex size-full flex-col overflow-hidden">
      <div className="flex h-leftColumnHeaderHeight items-center border-b border-primary/20 px-4">
        <h1 className="text-lg font-semibold">{t("settings")}</h1>
      </div>

      <div className="flex flex-col items-center justify-center gap-1 p-3 align-middle">
        <div className="relative p-3">
          {featureFlags.settingsMiddleViews ? (
            // Flat mode: plain photo, no actions — the uploader lives in the
            // Profile category view (slightly smaller than the uploader's
            // size-24, since it's display-only). Legacy accordion keeps the
            // size-24 uploader here.
            <Avatar
              aria-label={t("aria.avatar")}
              style={{ backgroundColor: `rgba(${avatarColor}, 0.7)` }}
              className="size-20"
            >
              <AvatarImage src={avatarUrl} alt="Avatar" />
              <AvatarFallback className="bg-transparent">
                {username.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          ) : (
            <AvatarUploader
              handleFileChange={handleFileChange}
              handleDeleteAvatar={deleteAvatar}
              isUploading={isUploading}
              blobUrl={blobUrl}
              name={username}
              defaultAvatarUrl={avatarUrl}
            />
          )}
        </div>
        <h4 className="font-semibold">{username}</h4>
        <Badge variant="secondary" className="text-xs">
          {PLAN_LABELS[plan] ?? plan}
        </Badge>
      </div>
      <ScrollArea className="w-full flex-1 p-3">
        {featureFlags.settingsMiddleViews ? (
          <>
            <FlatSettingsList />
            {/* The delete trigger has no font-size class of its own and
                inherits 16px — fine next to the 16px accordion triggers, but
                the flat list rows are text-sm. Wrap here (flat mode only) so
                the legacy accordion stays byte-identical. */}
            <div className="text-sm">
              <DeleteAccount />
            </div>
          </>
        ) : (
          <>
            <Accordion collapsible type="single" className="w-full">
              <AccountTab />
              <AppearanceTab />
              <LanguageTab />
              <PreferencesTab />
              <PaymentsTab />
            </Accordion>
            <DeleteAccount />
          </>
        )}
      </ScrollArea>
    </div>
  );
}
